import { useCallback, useEffect, useRef, useState } from 'react';
import { createCameraMediaActivitiesApi, setCameraMediaActivityStatus } from '../../services/cameraMediaActivitiesApi';
import { cameraPlacementDate } from '../../services/cameraMediaFeed';

const REFRESH_MS = 30000;
const AUTO_PAGES = 3; // without a host boundary, never silently drain all history
// Rows are exact videos; several can share one daily Live parent id.
const sameVideo = (a, b) => a._id === b._id && a.data.video.ref.id === b.data.video.ref.id;
// Live/starting rows carry live authority (Live badge, Watch live) that must be
// rechecked before display. A recording stays mounted through a visible recheck
// so its download or playback is not torn down; the answer still replaces it.
const holdsLiveAuthority = a => ['live', 'starting'].includes(a.data.video.state);
const empty = key => ({ key, items: [], cursor: null, exhausted: false, error: null, disabled: false, pages: 0, refreshing: false, withheld: false });

// Camera Media activities ('mine' or authorized 'feed') bound to ONE account
// key (identity + owner + scope). Rows of another key are never returned, not
// even for the first render after a login switch; every response is fenced by
// key and request token. Pages until the host feed's loaded boundary (or a
// bounded number of pages without one); refreshes the newest page only while
// visible, replacing changed rows by canonical id. Disabled camera = nothing;
// other failures are reported (error + retry), not shown as an empty archive.
export function useCameraMediaFeed(backendService, scope, boundary) {
  const identity = backendService?.authenticatedIdentity;
  const ownerId = backendService?.positionOwner;
  // Principal strings can repeat across service replacement and scope away/back.
  // Give each contiguous service/scope lifetime its own synchronous authority key.
  const lifetime = useRef({ backendService, identity, ownerId, scope, generation: 0 });
  if (lifetime.current.backendService !== backendService || lifetime.current.identity !== identity
    || lifetime.current.ownerId !== ownerId || lifetime.current.scope !== scope) {
    lifetime.current = { backendService, identity, ownerId, scope, generation: lifetime.current.generation + 1 };
  }
  const key = identity && ownerId ? `${identity}|${ownerId}|${scope}|${lifetime.current.generation}` : null;
  const [state, setState] = useState(() => empty(key));
  const inflight = useRef(null);
  const current = state.key === key ? state : empty(key);
  // Owner complete/reopen in progress or unresolved, per parent, for this key only.
  const [mutations, setMutations] = useState(() => ({ key, byId: {} }));
  const keyRef = useRef(key); keyRef.current = key;
  useEffect(() => {
    keyRef.current = key;
    return () => { if (keyRef.current === key) keyRef.current = null; };
  }, [key]);
  const generations = useRef(new Map());
  const requestRef = useRef(null);
  const queuedRefresh = useRef(null);

  const request = useCallback((mode, invalidate = false) => {
    if (!key || keyRef.current !== key) return;
    if (invalidate && inflight.current?.key === key) {
      // A mutation event is newer than this read. Coalesce any burst into one
      // successor, and do not publish the held pre-event success or failure.
      inflight.current.invalidated = true;
      queuedRefresh.current = key;
      setState(prev => prev.key === key ? { ...prev, refreshing: true } : prev);
      return;
    }
    if (inflight.current?.key === key && (mode !== 'refresh' || inflight.current.mode === 'refresh')) return;
    let api;
    try { api = createCameraMediaActivitiesApi({ backendService, identity, ownerId, isCurrent: () => keyRef.current === key }); } catch { return; }
    const token = {};
    inflight.current = { key, token, mode };
    const accepts = () => keyRef.current === key && inflight.current?.token === token && !inflight.current.invalidated;
    if (mode === 'refresh') setState(prev => prev.key === key ? { ...prev, refreshing: true } : prev);
    // 'reconcile' is a full reload after a lifecycle mutation: rows stay shown
    // (no authority recheck is due) while the persisted answer is fetched.
    const cursor = mode === 'more' ? current.cursor : null;
    // Refresh re-fetches the WHOLE loaded range from the first page and
    // replaces it atomically: a row absent from the current authorized answer
    // (deleted, revoked, narrowed) is withdrawn, never retained from cache.
    const reload = async () => {
      const items = []; let page = null; let pages = 0; let next = null;
      do {
        page = await api.list(scope, next);
        if (!accepts()) return null;
        for (const a of page.activities) if (!items.some(p => sameVideo(p, a))) items.push(a);
        pages += 1; next = page.nextCursor;
      } while (next && !page.exhausted && pages < Math.max(1, current.pages));
      return { items, pages, cursor: page.nextCursor, exhausted: page.exhausted || !page.nextCursor };
    };
    (mode === 'refresh' || mode === 'reconcile' ? reload() : api.list(scope, cursor)).then(page => {
      if (!accepts() || !page) return;
      // Confirmation retires the unresolved operation, not just its current
      // presentation: a later legitimate Start must not resurrect an old alert.
      const confirmedRows = page.items || page.activities;
      setMutations(prev => {
        if (prev.key !== key) return prev;
        const byId = { ...prev.byId };
        for (const [id, value] of Object.entries(byId)) {
          if (value.phase === 'unknown' && confirmedRows.some(a => String(a._id) === id
            && a.status === (value.transition === 'complete' ? 'complete' : 'pending'))) delete byId[id];
        }
        return { key, byId };
      });
      setState(prev => {
        const base = prev.key === key ? prev : empty(key);
        if (mode === 'refresh' || mode === 'reconcile') return { ...base, ...page, error: null, refreshing: false, withheld: false };
        const items = [...base.items];
        for (const a of page.activities) {
          const at = items.findIndex(p => sameVideo(p, a));
          if (at >= 0) items[at] = a; else items.push(a);
        }
        return { ...base, items, cursor: page.nextCursor, exhausted: page.exhausted || !page.nextCursor, error: null, pages: base.pages + 1 };
      });
    }, error => {
      if (!accepts()) return;
      const disabled = error?.response?.status === 503 && error.response.data?.code === 'CAMERA_DISABLED';
      // Without current authority, withhold every row (no stale Live/audience);
      // Retry starts again from the first page.
      setState(() => ({ ...empty(key), disabled, error: disabled ? null : 'Videos could not be loaded.' }));
    }).finally(() => {
      if (inflight.current?.token !== token) return;
      inflight.current = null;
      if (queuedRefresh.current === key && keyRef.current === key) {
        queuedRefresh.current = null;
        requestRef.current('refresh');
      }
    });
  }, [backendService, identity, ownerId, key, scope, current.cursor, current.pages]);

  // A completed explicit recording action invalidates only its owner's list;
  // do not wait for the thirty-second authority refresh to show a new parent.
  useEffect(() => {
    const changed = event => {
      if (event.detail?.identity === identity && event.detail?.ownerId === ownerId) request('refresh', true);
    };
    window.addEventListener('camera-media-changed', changed);
    return () => window.removeEventListener('camera-media-changed', changed);
  }, [identity, ownerId, request]);

  // Paging toward the host boundary.
  useEffect(() => {
    if (!key || current.exhausted || current.error || current.disabled || current.refreshing || document.visibilityState !== 'visible') return;
    const oldest = current.items.length ? cameraPlacementDate(current.items[current.items.length - 1]) : null;
    if (current.pages > 0 && boundary && oldest && new Date(oldest) < new Date(boundary)) return;
    if (current.pages > 0 && !boundary && current.pages >= AUTO_PAGES) return;
    request('more');
  });

  // Expired display authority is withheld even while hidden. Network refresh
  // runs only while visible; visibility return rechecks before restoring rows.
  useEffect(() => {
    if (!key || current.disabled) return undefined;
    const recheck = () => {
      const hidden = document.visibilityState !== 'visible';
      setState(prev => prev.key === key ? { ...prev, refreshing: true, withheld: prev.withheld || hidden } : prev);
      if (!hidden) request('refresh');
    };
    const timer = setInterval(recheck, REFRESH_MS);
    document.addEventListener('visibilitychange', recheck);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', recheck); };
  }, [key, current.disabled, request]);

  requestRef.current = request;
  // Persisted owner complete/reopen of one parent. A confirmed answer is shown at
  // once and every list response already in flight (read before this ACK) is
  // discarded; one fresh reload then lets the server answer win, including a
  // later legitimate reopen by an explicit Start. An unknown outcome keeps its
  // truthful state and reconciles from persisted data. Fenced by account key
  // and per-parent generation; nothing outlives an account change.
  const setStatus = useCallback(async (parentId, transition) => {
    if (!key) return 'stale';
    const mine = key; const id = String(parentId); const slot = `${mine}|${id}`;
    const generation = (generations.current.get(slot) || 0) + 1;
    generations.current.set(slot, generation);
    const live = () => keyRef.current === mine && generations.current.get(slot) === generation;
    const mark = value => setMutations(prev => {
      const byId = { ...(prev.key === mine ? prev.byId : {}) };
      if (value) byId[id] = value; else delete byId[id];
      return { key: mine, byId };
    });
    mark({ transition, phase: 'saving' });
    let result;
    try {
      result = await setCameraMediaActivityStatus({ backendService, identity, ownerId, isCurrent: live }, id, transition);
    } catch (error) {
      if (!live()) return 'stale';
      const outcome = ['rejected', 'unknown'].includes(error?.outcome) ? error.outcome : 'stale';
      if (outcome === 'stale') { mark(null); return 'stale'; }
      mark({ transition, phase: outcome, message: error.message });
      if (outcome === 'unknown') { inflight.current = null; queuedRefresh.current = null; requestRef.current('reconcile'); }
      return outcome;
    }
    if (!live()) return 'stale';
    inflight.current = null;
    queuedRefresh.current = null;
    setState(prev => prev.key !== mine ? prev : { ...prev,
      items: prev.items.map(a => String(a._id) === id ? { ...a, status: result.status, completed_at: result.completed_at } : a) });
    mark(null);
    requestRef.current('reconcile');
    return 'confirmed';
  }, [backendService, identity, ownerId, key]);
  // An unknown outcome resolves itself once the persisted row shows the target.
  const mutation = useCallback(id => {
    const value = mutations.key === key ? mutations.byId[String(id)] : null;
    if (!value) return null;
    const row = current.items.find(a => String(a._id) === String(id));
    if (value.phase === 'unknown' && row && row.status === (value.transition === 'complete' ? 'complete' : 'pending')) return null;
    return value;
  }, [mutations, key, current.items]);

  const retry = useCallback(() => setState(prev => (prev.key === key ? { ...prev, error: null } : prev)), [key]);
  const loadMore = useCallback(() => request('more'), [request]);
  const refresh = useCallback(() => request('refresh'), [request]);
  const hasMore = !current.exhausted && !current.disabled && !current.error;
  // Explicit continuation once auto-paging stops without a host boundary:
  // older videos stay reachable, never silently cut off or bulk-scanned.
  const canLoadMore = hasMore && !boundary && current.pages >= AUTO_PAGES;
  const items = !current.refreshing ? current.items : current.withheld ? [] : current.items.filter(a => !holdsLiveAuthority(a));
  return { items, checking: current.refreshing, error: current.error, hasMore, canLoadMore: canLoadMore && !current.refreshing, retry, loadMore, refresh, setStatus, mutation };
}
